import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MatDrawer } from '@angular/material/sidenav/drawer'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LogEvent, LogEventKey } from 'src/app/interfaces/log'
import { GeneralService } from 'src/app/services/general/general.service'
import { LocaleService } from 'src/app/services/locale/locale-service'
import { LogService } from 'src/app/services/log/log.service'
import { RouterService } from 'src/app/services/router/router.service'
import { UserService } from 'src/app/services/user/user.service'
import { environment } from 'src/environments/environment'
import { User } from './../../../interfaces/user'

interface Menu {
  image: string
  title: string
  group: number
  link?: string | 'app-install' //  app-install is for app download
  logEvent?: LogEventKey
  logParams: Record<string, string>
  ticker?: string
  tickerBackgroundColor?: string
}

const menuKeys = {
  ko: [
    'dailyLog',
    'hsp',
    'mbti',
    'ideal',
    'groupChemi',
    'guessMe',
    'attachmentType',
    'favourTournament',
    'cabinet',
    'imageGame',
    'friend',
    'suggest',
    'inquiry',
    'setting',
    'install',
  ],
  en: ['hsp', 'setting', 'install'],
  ja: ['hsp', 'setting', 'install'],
  zhHant: ['hsp', 'setting', 'install'],
}

@Component({
  selector: 'app-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
})
export class DrawerComponent implements OnInit, OnDestroy {
  constructor(
    public userService: UserService,
    public generalService: GeneralService,
    private routerService: RouterService,
    private logService: LogService,
    private localeService: LocaleService,
    private translate: TranslateService
  ) {}

  @ViewChild('drawer') drawer: MatDrawer | undefined
  drawerOpened = false
  user: User = this.userService.user
  menuList: Menu[] = []
  private drawerSubscription: Subscription | undefined
  private userSubscription: Subscription | undefined

  ngOnInit() {
    this.drawerSubscription = this.generalService.getDrawerSubject().subscribe((status: boolean) => {
      if (status) {
        this.toggleDrawer()
      } else {
        this.closeDrawer()
      }
    })
    this.userSubscription = this.userService.getUserSubject().subscribe((user: User) => {
      this.user = user
      this.setMenuList()
    })
  }

  ngOnDestroy(): void {
    if (this.drawerSubscription !== undefined) {
      this.drawerSubscription.unsubscribe()
    }
    if (this.userSubscription !== undefined) {
      this.userSubscription.unsubscribe()
    }
  }

  onClickMenu(menu: Menu): void {
    this.closeDrawer()
    const { link } = menu
    if (menu.logEvent) {
      this.logService.eventLogging(menu.logEvent, menu.logParams)
    }
    if (link !== undefined && link !== 'app-install') {
      if (this.generalService.isExternalLink(link)) {
        this.generalService.openInappBrowser(link)
        return
      }
      this.routerService.navigate(link)
      this.deprecatedRouterLogging(link)
      return
    }
  }

  onClickAlarm(): void {
    this.closeDrawer()
    this.routerService.navigate('alarm')
  }

  onClickProfile(): void {
    this.closeDrawer()
    this.routerService.navigate('profile')
  }

  private deprecatedRouterLogging(link: string): void {
    if (link.includes('quiz/create')) {
      this.logService.eventLogging(LogEvent.MENU_CLICK_QUIZ)
      return
    }
    if (link.includes('profile')) {
      this.logService.eventLogging(LogEvent.MENU_CLICK_MY)
      return
    }
    if (link.includes('fw/main')) {
      this.logService.eventLogging(LogEvent.MENU_CLICK_FW)
      return
    }
    if (link.includes('friends')) {
      this.logService.eventLogging(LogEvent.MENU_CLICK_FRIENDS)
      return
    }
    if (link.includes('setting')) {
      this.logService.eventLogging(LogEvent.MENU_CLICK_SETTING)
      return
    }
    this.logService.eventLogging(LogEvent.MENU_CLICK_MAIN)
  }

  private toggleDrawer(): void {
    this.drawerOpened = !this.drawerOpened
    this.drawer?.toggle()
  }

  private closeDrawer(): void {
    if (this.drawerOpened) {
      this.toggleDrawer()
    }
  }

  private async setMenuList(): Promise<void> {
    const keys: string[] = menuKeys[this.localeService.locale]
    this.menuList = []
    keys.forEach(async (key) => {
      this.menuList.push(await this.getMenuItem(key))
    })
  }

  private async getMenuItem(key: string): Promise<Menu> {
    switch (key) {
      case 'dailyLog':
        return {
          image: 'assets/icons/menu/icon-daily-log.png',
          title: '스꾸 다이어리',
          link: 'daily-log/landing',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '스꾸 다이어리',
          },
        }
      case 'hsp':
        return {
          image: 'assets/icons/menu/icon-menu-hsp.png',
          title: await this.translate.get(`common.drawer.hsp`).toPromise(),
          link: 'hsp',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '초민감자 성향도(HSP) 퀴즈',
          },
        }
      case 'mbti':
        return {
          image: 'assets/icons/menu/icon-menu-mbti.png',
          title: await this.translate.get(`common.drawer.mbti`).toPromise(),
          link: 'quiz/create',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '성격 유형 퀴즈',
          },
        }
      case 'ideal':
        return {
          image: 'assets/icons/menu/icon-menu-ideal-type.png',
          title: await this.translate.get(`common.drawer.ideal`).toPromise(),
          link: 'ideal/create',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '이상형 MBTI 궁합',
          },
        }
      case 'groupChemi':
        return {
          image: 'assets/icons/menu/icom-menu-group-chemi.png',
          title: await this.translate.get(`common.drawer.groupChemi`).toPromise(),
          link: 'group-chemi/create',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '우리 모임 MBTI 궁합',
          },
        }
      case 'guessMe':
        return {
          image: 'assets/icons/menu/icon-menu-guess-me.png',
          title: await this.translate.get(`common.drawer.guessMe`).toPromise(),
          link: 'guess-me/main',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '나를 맞혀봐',
            category: 'guess_me',
            purpose: 'visit_guess_me',
          },
        }
      case 'attachmentType':
        return {
          image: 'assets/icons/menu/icon-attachment-test.png',
          title: await this.translate.get(`common.drawer.attachmentType`).toPromise(),
          link: 'attachment-test',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '무료 애착 유형 검사',
            category: 'attachment_test',
            purpose: 'visit_attachment_test',
          },
        }
      case 'favourTournament':
        return {
          image: 'assets/icons/menu/icon-menu-worldcup.png',
          title: await this.translate.get(`common.drawer.favourTournament`).toPromise(),
          link: 'fw/main',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '호불호 월드컵',
          },
        }
      case 'cabinet':
        return {
          image: 'assets/icons/menu/icon-menu-cabinet.png',
          title: await this.translate.get(`common.drawer.cabinet`).toPromise(),
          link: 'cabinet/landing',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '캐비넷',
          },
        }
      case 'imageGame':
        return {
          image: 'assets/icons/menu/icon-menu-image-game.png',
          title: await this.translate.get(`common.drawer.imageGame`).toPromise(),
          link: 'image-game',
          group: 1,
          logEvent: LogEvent.DRAWER_SERVICE_ITEM_CLICKED,
          logParams: {
            title: '이미지 게임',
            category: 'image_game',
            purpose: 'visit_image_game',
          },
        }
      case 'friend':
        return {
          image: 'assets/icons/menu/icon-people-outline.png',
          title: await this.translate.get(`common.drawer.friend`).toPromise(),
          link: 'gnb/friends',
          group: 2,
          logEvent: LogEvent.DRAWER_SIMPLE_ITEM_CLICKED,
          logParams: {
            title: '친구',
          },
        }
      case 'suggest':
        return {
          image: 'assets/icons/menu/icon-feedback.png',
          title: await this.translate.get(`common.drawer.suggest`).toPromise(),
          link: environment.externalLink.suggest,
          group: 2,
          logEvent: LogEvent.DRAWER_SIMPLE_ITEM_CLICKED,
          logParams: {
            title: '타입스에 제안하기',
          },
        }
      case 'inquiry':
        return {
          image: 'assets/icons/menu/icon-help.png',
          title: await this.translate.get(`common.drawer.inquiry`).toPromise(),
          link: environment.externalLink.faq,
          group: 2,
          logEvent: LogEvent.DRAWER_SIMPLE_ITEM_CLICKED,
          logParams: {
            title: '문의하기',
          },
        }
      case 'setting':
        return {
          image: 'assets/icons/menu/icon-settings-outline.png',
          title: await this.translate.get(`common.drawer.setting`).toPromise(),
          link: 'setting',
          group: 2,
          logEvent: LogEvent.DRAWER_SIMPLE_ITEM_CLICKED,
          logParams: {
            title: '설정',
          },
        }
      default:
        return {
          image: 'assets/icons/icon-logo-default.png',
          title: await this.translate.get(`common.drawer.install`).toPromise(),
          link: 'app-install',
          group: 2,
          logEvent: LogEvent.DRAWER_SIMPLE_ITEM_CLICKED,
          logParams: {
            title: '앱 다운로드',
          },
        }
    }
  }
}
